import { NgbTimeStruct, NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import { DatePipe } from "@angular/common";

export interface NgbDateTimeStruct extends NgbDateStruct, NgbTimeStruct {}

export class DateTimeModel implements NgbDateTimeStruct {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;

  timeZoneOffset: number;

  public constructor(init?: Partial<DateTimeModel>) {
    Object.assign(this, init);
  }

  public static fromLocalStringInput(dateString: string): DateTimeModel {
    if (!dateString) return null;

    // Split date and time
    const parts = dateString.split(" ");
    if (parts.length !== 2) return null;

    const [datePart, timePart] = parts;

    // Manually parse `dd/MM/yyyy`
    const dateSegments = datePart.split("/");
    if (dateSegments.length !== 3) return null;

    // **Force** correct parsing order (DD/MM/YYYY)
    const [day, month, year] = dateSegments.map(Number);

    // Validate the parsed numbers
    if (isNaN(day) || isNaN(month) || isNaN(year) || month > 12 || day > 31) {
      return null; // Prevent incorrect values
    }

    // Ensure valid date
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day > daysInMonth) {
      return null; // Invalid day for month
    }

    // Parse time `HH:mm:ss`
    const timeSegments = timePart.split(":");
    if (timeSegments.length < 2 || timeSegments.length > 3) return null;

    const [hour, minute, second = 0] = timeSegments.map(Number);
    if (
      isNaN(hour) ||
      isNaN(minute) ||
      isNaN(second) ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59 ||
      second < 0 ||
      second > 59
    ) {
      return null;
    }

    return new DateTimeModel({
      year,
      month, // Correctly keeps `month` as the second value
      day, // Correctly keeps `day` as the first value
      hour,
      minute,
      second,
      timeZoneOffset: new Date().getTimezoneOffset(),
    });
  }

  public static fromLocalString(dateString: string): DateTimeModel {
    const date = new Date(dateString);
    const isValidDate = !isNaN(date.valueOf());
    if (!dateString || !isValidDate) {
      const parts = dateString.split(" ");
      if (parts.length !== 2) return null;

      const [datePart, timePart] = parts;

      // Parse the date part (d/M/yyyy)
      const dateSegments = datePart.split("/");
      if (dateSegments.length !== 3) return null;

      const [day, month, year] = dateSegments.map(Number);
      if (
        isNaN(day) ||
        isNaN(month) ||
        isNaN(year) ||
        day < 1 ||
        day > 31 ||
        month < 1 ||
        month > 12
      ) {
        return null; // Invalid date part
      }

      // Check if the day is valid for the month
      const daysInMonth = new Date(year, month, 0).getDate();
      if (day > daysInMonth) {
        return null; // Invalid day for the month
      }

      // Parse the time part (H:mm:ss)
      const timeSegments = timePart.split(":");
      if (timeSegments.length < 2 || timeSegments.length > 3) return null;

      const [hour, minute, second = 0] = timeSegments.map(Number);
      if (
        isNaN(hour) ||
        isNaN(minute) ||
        isNaN(second) ||
        hour < 0 ||
        hour > 23 ||
        minute < 0 ||
        minute > 59 ||
        second < 0 ||
        second > 59
      ) {
        return null; // Invalid time part
      }

      // Convert to ISO 8601 format: yyyy-MM-ddTHH:mm:ss
      const isoString = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}`;

      // Create a Date object to confirm validity
      const date = new Date(isoString);
      if (isNaN(date.valueOf())) {
        return null; // Invalid ISO string
      }

      // Return a valid DateTimeModel object
      return new DateTimeModel({
        year,
        month,
        day,
        hour,
        minute,
        second,
        timeZoneOffset: date.getTimezoneOffset(),
      });
    }
    return new DateTimeModel({
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
      timeZoneOffset: date.getTimezoneOffset(),
    });
  }

  private isInteger(value: any): value is number {
    return (
      typeof value === "number" &&
      isFinite(value) &&
      Math.floor(value) === value
    );
  }

  public toString(): string {
    if (
      this.isInteger(this.year) &&
      this.isInteger(this.month) &&
      this.isInteger(this.day)
    ) {
      const year = this.year.toString().padStart(2, "0");
      const month = this.month.toString().padStart(2, "0");
      const day = this.day.toString().padStart(2, "0");

      if (!this.hour) {
        this.hour = 0;
      }
      if (!this.minute) {
        this.minute = 0;
      }
      if (!this.second) {
        this.second = 0;
      }
      if (!this.timeZoneOffset) {
        this.timeZoneOffset = new Date().getTimezoneOffset();
      }

      const hour = this.hour.toString().padStart(2, "0");
      const minute = this.minute.toString().padStart(2, "0");
      const second = this.second.toString().padStart(2, "0");

      const tzo = -this.timeZoneOffset;
      const dif = tzo >= 0 ? "+" : "-",
        pad = function (num) {
          const norm = Math.floor(Math.abs(num));
          return (norm < 10 ? "0" : "") + norm;
        };

      const isoString = `${pad(year)}-${pad(month)}-${pad(day)}T${pad(
        hour
      )}:${pad(minute)}:${pad(second)}${dif}${pad(tzo / 60)}:${pad(tzo % 60)}`;
      return isoString;
    }

    return null;
  }
}
